import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { FilterBar, FilterSelect } from '@/components/feature/FilterBar';
import PaperPreviewModal from '@/pages/gcse/past-papers/components/PaperPreviewModal';

type Board = 'AQA' | 'OCR';
type Level = 'A-Level' | 'AS-Level';
type Year = string;

interface PastPaper {
  id: string;
  board: Board;
  level: Level;
  year: Year;
  paper: string;
  title: string;
  type: string;
  pages: number;
  size: string;
  fileUrl: string;
}

const pastPapers: PastPaper[] = [];

const boardColors: Record<Board, { badge: string; bg: string; border: string }> = {
  AQA: { badge: 'bg-rose-100 text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200' },
  OCR: { badge: 'bg-teal-100 text-teal-700', bg: 'bg-teal-50', border: 'border-teal-200' },
};

const typeColors: Record<string, string> = {
  'Question Paper': 'bg-slate-100 text-slate-700',
  'Mark Scheme': 'bg-emerald-100 text-emerald-700',
  'Examiner Report': 'bg-violet-100 text-violet-700',
};

const typeIcons: Record<string, string> = {
  'Question Paper': 'ri-file-text-line',
  'Mark Scheme': 'ri-check-double-line',
  'Examiner Report': 'ri-bar-chart-line',
};

const boards: Board[] = ['AQA', 'OCR'];
const levels: Level[] = ['A-Level', 'AS-Level'];
const years: Year[] = ['2024', '2023', '2022', '2019', '2018'];

export default function ALevelPastPapersPage() {
  const [pastPapers, setPastPapers] = useState<PastPaper[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBoard, setSelectedBoard] = useState<Board | 'All'>('All');
  const [selectedLevel, setSelectedLevel] = useState<Level | 'All'>('All');
  const [selectedYear, setSelectedYear] = useState<string>('All');
  const [selectedType, setSelectedType] = useState<string>('All');
  const [previewPaper, setPreviewPaper] = useState<PastPaper | null>(null);

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001';

  useEffect(() => {
    const fetchPapers = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/pastpapers`);
        if (res.ok) {
          const data = await res.json();
          const al = data.filter((p: any) => p.level === 'A-Level' || p.level === 'AS-Level').map((p: any) => ({
            id: p.id || p._id,
            board: p.board,
            level: p.level,
            year: p.year?.toString() || 'N/A',
            paper: p.tags?.split(',')[0] || 'Paper',
            title: p.title,
            type: p.type,
            pages: p.pages || 0,
            size: p.size || '0.1 MB',
            fileUrl: p.fileUrl
          }));
          setPastPapers(al);
        }
      } catch (err) {
        console.error('Failed to fetch a-level papers:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPapers();
  }, [API_BASE]);

  const filtered = pastPapers.filter((p) => {
    if (selectedBoard !== 'All' && p.board !== selectedBoard) return false;
    if (selectedLevel !== 'All' && p.level !== selectedLevel) return false;
    if (selectedYear !== 'All' && p.year !== selectedYear) return false;
    if (selectedType !== 'All' && p.type !== selectedType) return false;
    return true;
  });

  const grouped = filtered.reduce<Record<string, PastPaper[]>>((acc, p) => {
    const key = `${p.board} ${p.level} ${p.year}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />

      {/* Hero */}
      <section className="relative bg-slate-900 pt-24 pb-10 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20dark%20background%20with%20stacked%20exam%20papers%20and%20academic%20documents%2C%20deep%20navy%20gradient%2C%20sophisticated%20geometric%20patterns%2C%20clean%20minimal%20design%2C%20professional%20academic%20atmosphere%2C%20subtle%20teal%20accents&width=1440&height=280&seq=alpapers1&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/85 to-slate-900/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">A Level Resources</span>
            <i className="ri-arrow-right-s-line text-slate-500 text-xs"></i>
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Past Papers</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
            A Level Past Papers
          </h1>
          <p className="text-slate-400 text-base max-w-2xl">
            Past exam papers and mark schemes for AQA and OCR A Level and AS Level Computer Science. Filter by board, level and year.
          </p>
        </div>
      </section>

      {/* Filters */}
      <FilterBar count={filtered.length} countLabel="papers">
        <FilterSelect
          label="Board"
          value={selectedBoard}
          onChange={(v) => setSelectedBoard(v as Board | 'All')}
          options={[{ value: 'All', label: 'All Boards' }, ...boards.map((b) => ({ value: b, label: b }))]}
        />
        <FilterSelect
          label="Level"
          value={selectedLevel}
          onChange={(v) => setSelectedLevel(v as Level | 'All')}
          options={[{ value: 'All', label: 'All Levels' }, ...levels.map((l) => ({ value: l, label: l }))]}
        />
        <FilterSelect
          label="Year"
          value={selectedYear}
          onChange={(v) => setSelectedYear(v as Year | 'All')}
          options={[{ value: 'All', label: 'All Years' }, ...years.map((y) => ({ value: y, label: y }))]}
        />
        <FilterSelect
          label="Type"
          value={selectedType}
          onChange={(v) => setSelectedType(v)}
          options={[
            { value: 'All', label: 'All Types' },
            { value: 'Question Paper', label: 'Question Paper' },
            { value: 'Mark Scheme', label: 'Mark Scheme' },
            { value: 'Examiner Report', label: 'Examiner Report' },
          ]}
        />
      </FilterBar>

      {/* Content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 md:px-6 py-10 w-full">
        {Object.keys(grouped).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-slate-100 rounded-2xl mb-4">
              <i className="ri-file-search-line text-slate-400 text-2xl"></i>
            </div>
            <p className="text-slate-600 font-medium mb-1">No papers found</p>
            <p className="text-slate-400 text-sm">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            {Object.entries(grouped).map(([groupKey, papers]) => {
              const board = papers[0].board;
              const level = papers[0].level;
              return (
                <div key={groupKey}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold border ${boardColors[board].badge} border-current`}>{board}</span>
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-600">{level}</span>
                    <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: "'Inter', sans-serif" }}>{papers[0].year}</h2>
                    <span className="text-sm text-slate-400">{papers.length} file{papers.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {papers.map((paper) => (
                      <div key={paper.id} className="bg-white rounded-xl border border-slate-100 p-5 hover:border-emerald-200 transition-colors group">
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="w-10 h-10 flex items-center justify-center bg-slate-50 rounded-lg flex-shrink-0 group-hover:bg-emerald-50 transition-colors">
                            <i className={`${typeIcons[paper.type]} text-slate-500 group-hover:text-emerald-600 text-lg transition-colors`}></i>
                          </div>
                          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${typeColors[paper.type]}`}>{paper.type}</span>
                        </div>
                        <p className="text-xs font-semibold text-slate-500 mb-1">{paper.paper}</p>
                        <h3 className="text-sm font-bold text-slate-800 mb-3 leading-snug">{paper.title}</h3>
                        <div className="flex items-center gap-3 text-xs text-slate-400 mb-4">
                          <span className="flex items-center gap-1"><i className="ri-pages-line"></i>{paper.pages} pages</span>
                          <span className="flex items-center gap-1"><i className="ri-file-line"></i>{paper.size}</span>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setPreviewPaper(paper)}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-all cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-eye-line text-sm"></i>
                            Preview
                          </button>
                          <button className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-medium transition-all cursor-pointer whitespace-nowrap">
                            <i className="ri-download-line text-sm"></i>
                            Download
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <PaperPreviewModal
        paper={previewPaper ? { ...previewPaper, level: previewPaper.level } : null}
        onClose={() => setPreviewPaper(null)}
        typeColors={typeColors}
        typeIcons={typeIcons}
        boardBadgeColor={previewPaper ? boardColors[previewPaper.board].badge : ''}
      />

      <Footer />
    </div>
  );
}
